import React, { useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  setPersistence, 
  browserSessionPersistence 
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, database } from './firebaseConfig';
import './KurumsalLogin.css';
import './Modal.css';

const KurumsalLogin = () => {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState(''); // Şifre tekrarı
  const [showPassword, setShowPassword] = useState(false); // Şifre göster/gizle
  const [acceptedTerms, setAcceptedTerms] = useState(false); // Sözleşme kabulü
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' veya 'error'
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const navigate = useNavigate();

  // Oturum kontrolü
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        // Kurumsal kullanıcı mı kontrol et
        try {
          const corporateUserRef = doc(database, 'corporateUsers', user.email);
          const corporateUserDoc = await getDoc(corporateUserRef);
          
          if (corporateUserDoc.exists()) {
            // Kurumsal kullanıcı, puantajlar sayfasına yönlendir
            navigate('/kurumsal/puantajlar');
          }
        } catch (error) {
          console.error('Kurumsal kullanıcı kontrolü hatası:', error);
        }
      }
      setInitialLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  // Basit normalize yardımcıları (log anahtarı üretimi için)
  const normalizeDigits = (value) => {
    if (!value) return '';
    return value.toString().replace(/\D/g, '');
  };

  const normalizeCompanyKey = (companyNameValue, taxNumberValue, emailValue) => {
    const tn = normalizeDigits(taxNumberValue);
    if (tn) return `tax:${tn}`;
    const cn = (companyNameValue || '').toString().trim().toLowerCase();
    if (cn) return `name:${cn}`;
    const em = (emailValue || '').toString().trim().toLowerCase();
    if (em) return `email:${em}`;
    return 'unknown';
  };

  // Kurumsal giriş logu kaydetme fonksiyonu
  const saveCorporateLoginLog = async (userEmail, companyName, taxNumberValue) => {
    try {
      const clientNowIso = new Date().toISOString();
      const logData = {
        email: userEmail,
        companyName: companyName || '-',
        taxNumber: normalizeDigits(taxNumberValue),
        companyKey: normalizeCompanyKey(companyName, taxNumberValue, userEmail),
        source: 'kurumsal-login',
        userAgent: typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '',
        loginTime: serverTimestamp(),
        timestamp: clientNowIso,
        createdAt: clientNowIso
      };
      
      const docRef = await addDoc(collection(database, 'corporateLoginLogs'), logData);
      console.log('✅ Kurumsal giriş logu kaydedildi:', docRef.id, logData);
    } catch (error) {
      console.error('❌ Kurumsal giriş logu kaydedilemedi:', error);
      console.error('Hata detayları:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
    }
  };

  // Kurumsal kullanıcı dokümanına son giriş zamanını yaz (admin panelde net görünsün)
  const updateCorporateUserLastLogin = async (userEmail) => {
    try {
      const corporateUserRef = doc(database, 'corporateUsers', userEmail);
      await updateDoc(corporateUserRef, {
        lastLoginTime: serverTimestamp(),
        lastLoginClientTime: new Date().toISOString()
      });
    } catch (error) {
      // Log yazımı başarısız olsa bile giriş devam etsin
      console.warn('Kurumsal kullanıcı lastLoginTime güncellenemedi:', error?.message || error);
    }
  };

  // Kayıt işlemi
  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validasyonlar
    if (!email || !password || !confirmPassword || !companyName || !taxNumber) {
      showMessage('Lütfen tüm alanları doldurun.', 'error');
      setLoading(false);
      return;
    }

    // Sözleşme kabul kontrolü
    if (!acceptedTerms) {
      showMessage('Kayıt olmak için sözleşmeleri kabul etmeniz gerekmektedir.', 'error');
      setLoading(false);
      return;
    }

    // Şifre eşleşme kontrolü
    if (password !== confirmPassword) {
      showMessage('Şifreler eşleşmiyor.', 'error');
      setLoading(false);
      return;
    }

    // Vergi numarası validasyonu (10 veya 11 haneli olmalı)
    const cleanTaxNumber = taxNumber.replace(/\D/g, '');
    if (cleanTaxNumber.length !== 10 && cleanTaxNumber.length !== 11) {
      showMessage('Vergi numarası 10 veya 11 haneli olmalıdır.', 'error');
      setLoading(false);
      return;
    }

    try {
      // Firebase Auth ile kullanıcı oluştur
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Firestore'da kurumsal kullanıcı kaydı oluştur
      await setDoc(doc(database, 'corporateUsers', email), {
        email: email,
        uid: userCredential.user.uid,
        companyName: companyName,
        taxNumber: cleanTaxNumber,
        createdAt: new Date().toISOString(),
        isVerified: false, // Vergi numarası ile eşleşme yapılmadı
        linkedOwnerEmail: null // Hangi firma sahibine bağlı
      });

      showMessage('Kayıt başarılı! Giriş yapabilirsiniz.', 'success');
      
      // Giriş moduna geç
      setTimeout(() => {
        setIsLoginMode(true);
        setPassword('');
        setConfirmPassword('');
      }, 2000);

    } catch (error) {
      console.error('Kayıt hatası:', error);
      if (error.code === 'auth/email-already-in-use') {
        showMessage('Bu e-posta adresi zaten kullanılıyor.', 'error');
      } else if (error.code === 'auth/weak-password') {
        showMessage('Şifre en az 6 karakter olmalıdır.', 'error');
      } else {
        showMessage('Kayıt başarısız: ' + error.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Giriş işlemi
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await setPersistence(auth, browserSessionPersistence);
      await signInWithEmailAndPassword(auth, email, password);

      // Kurumsal kullanıcı kontrolü
      const corporateUserRef = doc(database, 'corporateUsers', email);
      const corporateUserDoc = await getDoc(corporateUserRef);

      if (!corporateUserDoc.exists()) {
        showMessage('Bu hesap kurumsal kullanıcı olarak kayıtlı değil.', 'error');
        await auth.signOut();
        setLoading(false);
        return;
      }

      // Kurumsal giriş logunu kaydet
      const corporateUserData = corporateUserDoc.data();
      await saveCorporateLoginLog(
        email,
        corporateUserData.companyName || '-',
        corporateUserData.taxNumber || ''
      );
      await updateCorporateUserLastLogin(email);

      // Puantajlar sayfasına yönlendir
      navigate('/kurumsal/puantajlar');

    } catch (error) {
      console.error('Giriş hatası:', error);
      if (error.code === 'auth/user-not-found') {
        showMessage('Bu e-posta adresi ile kayıtlı kullanıcı bulunamadı.', 'error');
      } else if (error.code === 'auth/wrong-password') {
        showMessage('Hatalı şifre.', 'error');
      } else {
        showMessage('Giriş başarısız: ' + error.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <section className="kurumsal-section">
        <div className="kurumsal-wave">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div className="kurumsal-logo-container">
          <h1 className="kurumsal-logo-text">Hops Software</h1>
          <span className="kurumsal-badge">KURUMSAL</span>
        </div>
        <div className="kurumsal-container loading-container">
          <div className="loading-spinner-wrapper">
            <div className="loading-spinner"></div>
            <div className="loading-text">Yükleniyor...</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="kurumsal-section">
      <div className="kurumsal-wave">
        <span></span>
        <span></span>
        <span></span>
      </div>
      
      {/* Logo */}
      <div className="kurumsal-logo-container">
        <h1 className="kurumsal-logo-text" onClick={() => window.location.href = 'https://www.hopssoftware.com'}>
          Hops Software
        </h1>
        <span className="kurumsal-badge">KURUMSAL</span>
      </div>
      
      <div className="kurumsal-container">
        {/* Tab Seçimi */}
        <div className="kurumsal-tabs">
          <button 
            className={`kurumsal-tab ${isLoginMode ? 'active' : ''}`}
            onClick={() => setIsLoginMode(true)}
          >
            Giriş Yap
          </button>
          <button 
            className={`kurumsal-tab ${!isLoginMode ? 'active' : ''}`}
            onClick={() => setIsLoginMode(false)}
          >
            Kayıt Ol
          </button>
        </div>

        <div className="kurumsal-form-container">
          {isLoginMode ? (
            // Giriş Formu
            <form onSubmit={handleLogin} className="kurumsal-form">
              <h2>Kurumsal Giriş</h2>
              <p className="kurumsal-description">
                Kurumsal hesabınızla giriş yaparak size ait puantajları görüntüleyebilirsiniz.
              </p>
              
              <input
                type="email"
                placeholder="E-posta Adresi"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="kurumsal-input"
              />

              <input
                type={showPassword ? "text" : "password"}
                placeholder="Şifre"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="kurumsal-input"
              />
              
              <div className="show-password-checkbox">
                <input
                  type="checkbox"
                  id="showPasswordLogin"
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                />
                <label htmlFor="showPasswordLogin">Şifreyi göster</label>
              </div>

              <button type="submit" className="kurumsal-button" disabled={loading}>
                {loading ? (
                  <div className="button-loading">
                    <div className="button-spinner"></div>
                    <span>Giriş Yapılıyor...</span>
                  </div>
                ) : (
                  'Giriş Yap'
                )}
              </button>
            </form>
          ) : (
            // Kayıt Formu
            <form onSubmit={handleSignup} className="kurumsal-form">
              <h2>Kurumsal Kayıt</h2>
              <p className="kurumsal-description">
                Kurumsal hesap oluşturarak firmanıza ait puantajları takip edebilirsiniz.
              </p>
              
              <input
                type="text"
                placeholder="Firma Adı"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                className="kurumsal-input"
              />

              <input
                type="text"
                placeholder="Vergi Numarası (10-11 hane)"
                value={taxNumber}
                onChange={(e) => setTaxNumber(e.target.value.replace(/\D/g, '').slice(0, 11))}
                required
                className="kurumsal-input"
                maxLength="11"
              />

              <input
                type="email"
                placeholder="E-posta Adresi"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="kurumsal-input"
              />

              <input
                type={showPassword ? "text" : "password"}
                placeholder="Şifre (en az 6 karakter)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="kurumsal-input"
                minLength="6"
              />

              <input
                type={showPassword ? "text" : "password"}
                placeholder="Şifre Tekrarı"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className={`kurumsal-input ${confirmPassword && password !== confirmPassword ? 'input-error' : ''}`}
                minLength="6"
              />
              
              {confirmPassword && password !== confirmPassword && (
                <small className="kurumsal-input-error">Şifreler eşleşmiyor</small>
              )}
              
              <div className="show-password-checkbox">
                <input
                  type="checkbox"
                  id="showPasswordSignup"
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                />
                <label htmlFor="showPasswordSignup">Şifreleri göster</label>
              </div>

              <div className="terms-checkbox">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={acceptedTerms}
                  onChange={() => setAcceptedTerms(!acceptedTerms)}
                />
                <label htmlFor="acceptTerms">
                  <a href="#privacy" onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}>Gizlilik Sözleşmesi</a>
                  {' '}ve{' '}
                  <a href="#terms" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}>Kullanım Koşulları</a>
                  'nı okudum ve kabul ediyorum.
                </label>
              </div>

              <button type="submit" className="kurumsal-button" disabled={loading || (confirmPassword && password !== confirmPassword) || !acceptedTerms}>
                {loading ? (
                  <div className="button-loading">
                    <div className="button-spinner"></div>
                    <span>Kayıt Yapılıyor...</span>
                  </div>
                ) : (
                  'Kayıt Ol'
                )}
              </button>
            </form>
          )}
        </div>

        {/* Mesaj Gösterimi */}
        {message && (
          <div className={`kurumsal-message ${messageType}`}>
            {message}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <footer className="kurumsal-footer">
        <div className="footer-content">
          <p>&copy; 2024 Hops Software. Tüm hakları saklıdır.</p>
          <div className="footer-links">
            <a href="https://www.hopssoftware.com" className="footer-link" target="_blank" rel="noopener noreferrer">
              Ana Sayfa
            </a>
            <span className="footer-separator">•</span>
            <a href="/" className="footer-link">
              Firma Girişi
            </a>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="legal-modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="legal-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="legal-modal-close" onClick={() => setShowPrivacyModal(false)}>×</button>
            <div className="legal-modal-body">
              <h2 className="legal-modal-title">Gizlilik Sözleşmesi</h2>
              
              <h3>1. Veri Toplama ve Kullanım</h3>
              <p>HOPS olarak, hizmetlerimizi kullanırken sizden toplanan kişisel verilerin gizliliğini korumayı taahhüt ediyoruz. Bu veriler arasında isim, e-posta adresi, telefon numarası ve şirket bilgileri bulunmaktadır.</p>
              
              <h3>2. Veri Güvenliği</h3>
              <p>Verileriniz, endüstri standardı güvenlik protokolleri ve şifreleme teknolojileri kullanılarak korunmaktadır. Yetkisiz erişim, değiştirme veya ifşaya karşı uygun güvenlik önlemleri alınmaktadır.</p>
              
              <h3>3. Veri Paylaşımı</h3>
              <p>Kişisel verileriniz, yasal zorunluluklar dışında üçüncü taraflarla paylaşılmamaktadır. Hizmet sağlayıcılarımızla yapılan veri paylaşımları, gizlilik sözleşmeleri kapsamında gerçekleştirilmektedir.</p>
              
              <h3>4. Çerezler ve İzleme</h3>
              <p>Platformumuzda, kullanıcı deneyimini iyileştirmek için çerezler kullanılmaktadır. Bu çerezler, oturum bilgilerini ve kullanıcı tercihlerini saklamak için kullanılır.</p>
              
              <h3>5. Kullanıcı Hakları</h3>
              <p>KVKK kapsamında, kişisel verilerinize erişim, düzeltme, silme ve işleme itiraz etme haklarına sahipsiniz. Bu haklarınızı kullanmak için bizimle iletişime geçebilirsiniz.</p>
              
              <div className="legal-modal-contact">
                <h3>İletişim Bilgileri</h3>
                <p>Gizlilik politikamız hakkında sorularınız için:</p>
                <p>E-posta: info@hopssoftware.com</p>
                <p>Telefon: +90 552 464 11 16</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms Modal */}
      {showTermsModal && (
        <div className="legal-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="legal-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="legal-modal-close" onClick={() => setShowTermsModal(false)}>×</button>
            <div className="legal-modal-body">
              <h2 className="legal-modal-title">Mesafeli Satış Sözleşmesi</h2>
              
              <h3>1. Taraflar</h3>
              <p>İşbu sözleşme, HOPS (bundan sonra SATICI olarak anılacaktır) ile alıcı (bundan sonra ALICI olarak anılacaktır) arasında aşağıdaki şartlar çerçevesinde düzenlenmiştir.</p>
              
              <h3>2. Sözleşmenin Konusu</h3>
              <p>İşbu sözleşmenin konusu, ALICI'nın SATICI'ya ait platform üzerinden satın aldığı kontör hizmetinin satışı ve teslimi ile ilgili olarak 6502 Sayılı Tüketicinin Korunması Hakkında Kanun hükümleri gereğince tarafların hak ve yükümlülüklerinin belirlenmesidir.</p>
              
              <h3>3. Hizmet Bilgileri</h3>
              <p>Satın alınan kontör paketi, dijital bir hizmet olup, ödemenin tamamlanmasını takiben ALICI'nın hesabına otomatik olarak tanımlanacaktır. Kontörler, aktivasyon tarihinden itibaren süresiz olarak geçerlidir.</p>
              
              <h3>4. Ödeme ve Teslimat</h3>
              <p>Ödeme, platform üzerinden kredi kartı veya banka kartı ile gerçekleştirilir. Kontörler, ödemenin başarıyla tamamlanmasının ardından anında hesaba tanımlanır. Dijital hizmet olması sebebiyle fiziksel teslimat söz konusu değildir.</p>
              
              <h3>5. Cayma Hakkı</h3>
              <p>Dijital içerik/hizmet sunumu niteliğinde olan kontör satışlarında, hizmetin tesliminin/ifasının başladığı an itibarıyla cayma hakkı kullanılamaz. Bu husus 6502 Sayılı Kanun'un 15/1-ğ maddesi uyarınca cayma hakkı istisnası kapsamındadır.</p>
              
              <div className="legal-modal-contact">
                <h3>Satıcı Bilgileri</h3>
                <p>Unvan: TUĞBA HAZAL ÖZKARAASLAN HOPS YAZILIM SİSTEMLERİ TEKNOPARK ŞUBESİ</p>
                <p>Adres: Yeniköy Merkez Mah. Vatan Cad. Teknopark Sitesi Başiskele / Kocaeli</p>
                <p>E-posta: info@hopssoftware.com</p>
                <p>Telefon: +90 552 464 11 16</p>
                <p>MERSİS No: 3888146357 / 154133525540002</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default KurumsalLogin;



